import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PulsarService } from "@/services/pulsar.service";
import { SocialUser, CreateTelegramUser } from "@/types/social-pay.types";
import { useConnection } from "@/contexts/connection-context";
import { useAuthContext } from "@/contexts/auth-context";
import { toast } from "sonner";
import { getErrorMessage } from "@/helpers/getErrorMessage";

export const useGetSocialUser = () => {
  const { isAuthenticated, walletAddress } = useConnection();
  const { accessToken } = useAuthContext();

  return useQuery<SocialUser>({
    queryKey: ["social-pay-user", walletAddress],
    queryFn: async () => {
      const user = (await PulsarService.getSocialUser()).data;
      return user;
    },
    staleTime: 1000 * 30, // 30 seconds
    enabled: isAuthenticated && !!walletAddress && !!accessToken,
    retry: (failureCount, error: unknown) => {
      // Don't retry on 401 errors (authentication issues)
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { status?: number } };
        if (axiosError.response?.status === 401) {
          return false;
        }
      }
      return failureCount < 3;
    },
  });
};

export const useSocialPayMutations = () => {
  const queryClient = useQueryClient();

  const linkTwitter = useMutation({
    mutationFn: async (body: { redirectUrl: string }) => {
      const resp = await PulsarService.registerTwitter(body.redirectUrl);
      const url = resp.data;
      if (!url) {
        throw new Error("No OAuth URL present");
      }
      window.location.href = url;
    },
    onError: (e) => {
      toast.error(getErrorMessage(e, "Failed to link Twitter account"));
    },
  });

  const linkTelegram = useMutation({
    mutationFn: async (user: CreateTelegramUser) => {
      const resp = await PulsarService.registerTelegram(user);
      const url = resp.data;
      if (!url) {
        throw new Error("No OAuth URL present");
      }
      queryClient.invalidateQueries({
        queryKey: ["social-pay-user"],
      });
    },
  });

  const unlinkTelegram = useMutation({
    mutationFn: async () => {
      await PulsarService.unlinkTelegram();
      toast.success("Telegram account unlinked");
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["social-pay-user"],
      });
    },
  });

  const unlinkTwitter = useMutation({
    mutationFn: async () => {
      await PulsarService.unlinkTwitter();
      toast.success("Twitter account unlinked");
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["social-pay-user"],
      });
    },
  });

  const unlinkDiscord = useMutation({
    mutationFn: async () => {
      await PulsarService.unregisterDiscord();
      toast.success("Discord account unlinked");
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["social-pay-user"],
      });
    },
  });

  const linkDiscord = useMutation({
    mutationFn: async () => {
      const redirectUrl = window.location.href;
      const resp = await PulsarService.registerDiscord(redirectUrl);
      const url = resp.data;
      if (!url) {
        throw new Error("No OAuth URL present");
      }
      window.location.href = url;
    },
    onError: (e) => {
      toast.error(getErrorMessage(e, "Failed to link Discord account"));
    },
  });

  return {
    linkTwitter,
    unlinkTwitter,
    linkTelegram,
    unlinkTelegram,
    linkDiscord,
    unlinkDiscord,
  };
};
